import { useEffect, useState } from 'react';
import {
  Body1,
  Button,
  Input,
  MessageBar,
  MessageBarBody,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  tokens,
} from '@fluentui/react-components';
import { CheckmarkRegular, DismissRegular } from '@fluentui/react-icons';
import { useApi } from '../api/useApi';
import { useAsync } from '../api/useAsync';
import { PageHeader } from '../components/PageHeader';
import type { ApprovalRequestDto } from '../api/types';

export function ApprovalsPage() {
  const api = useApi();
  const { data, loading, error } = useAsync(() => api.getPendingApprovals(), [api]);
  const [rows, setRows] = useState<ApprovalRequestDto[]>([]);
  const [comments, setComments] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (data) setRows(data);
  }, [data]);

  async function decide(id: string, approve: boolean) {
    setBusy(id);
    setActionError(null);
    try {
      const comment = comments[id]?.trim() || undefined;
      if (approve) {
        await api.approveRequest(id, comment);
      } else {
        await api.rejectRequest(id, comment);
      }
      setRows((rs) => rs.filter((r) => r.id !== id));
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div>
      <PageHeader title="Zu genehmigen" description="Offene Anträge, die du freigeben darfst (4-Augen-Prinzip)." />
      {loading ? <Spinner label="Lade…" /> : null}
      {error ? (
        <MessageBar intent="error">
          <MessageBarBody>{error.message}</MessageBarBody>
        </MessageBar>
      ) : null}
      {actionError ? (
        <MessageBar intent="error">
          <MessageBarBody>{actionError}</MessageBarBody>
        </MessageBar>
      ) : null}
      {rows.length === 0 && !loading ? <Body1>Keine offenen Anträge für dich.</Body1> : null}
      {rows.length > 0 ? (
        <Table aria-label="Zu genehmigen" size="small">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Erstellt</TableHeaderCell>
              <TableHeaderCell>Antragsteller</TableHeaderCell>
              <TableHeaderCell>Aktion</TableHeaderCell>
              <TableHeaderCell>Ticket</TableHeaderCell>
              <TableHeaderCell>Kommentar</TableHeaderCell>
              <TableHeaderCell>Entscheidung</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id}>
                <TableCell>{new Date(r.createdUtc).toLocaleString('de-DE')}</TableCell>
                <TableCell>
                  <span className="pii">{r.requesterUpn}</span>
                </TableCell>
                <TableCell>
                  <span className="pii">{r.targetSummary}</span>
                </TableCell>
                <TableCell>{r.ticketNumber ?? '—'}</TableCell>
                <TableCell>
                  <Input
                    size="small"
                    placeholder="optional"
                    value={comments[r.id] ?? ''}
                    onChange={(_, d) => setComments((c) => ({ ...c, [r.id]: d.value }))}
                    style={{ width: '160px' }}
                  />
                </TableCell>
                <TableCell>
                  <div style={{ display: 'flex', gap: tokens.spacingHorizontalXS }}>
                    <Button
                      size="small"
                      appearance="primary"
                      icon={<CheckmarkRegular />}
                      disabled={busy === r.id}
                      onClick={() => decide(r.id, true)}
                    >
                      Genehmigen
                    </Button>
                    <Button
                      size="small"
                      icon={<DismissRegular />}
                      disabled={busy === r.id}
                      onClick={() => decide(r.id, false)}
                    >
                      Ablehnen
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}
    </div>
  );
}
